import { Input } from '@/components/ui/FormControls';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  useCashbook,
  useExpenseCategories,
  useAccountsSummary,
} from '@/hooks/useAccounts';
import { useVoidCashTransaction } from '@/hooks/useCash';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import EmptyState from '@/components/common/EmptyState';
import ExportActions from '@/components/common/ExportActions';
import {
  CashbookRow,
  formatINR,
  formatDisplayDate,
  parseRpcError,
} from '@/utils/cashbookCalc';
import {
  buildCashbookExcelOptions,
  buildCashbookPdfOptions,
} from '@/utils/cashbookExport';
import type { AccountsContextValue } from '../AccountsLayout';

interface ExpensesTabProps {
  from?: string | null;
  to?: string | null;
  onOpenRecordExpense?: (categoryId?: string) => void;
}

export default function ExpensesTab(props: ExpensesTabProps) {
  const ctx = useOutletContext<AccountsContextValue | undefined>();
  const from = props.from !== undefined ? props.from : ctx?.from ?? null;
  const to = props.to !== undefined ? props.to : ctx?.to ?? null;
  const onOpenRecordExpense = props.onOpenRecordExpense ?? ctx?.openExpenseModal ?? (() => {});
  const { role, organization } = useAuth();
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const confirm = useConfirm();
  const voidMutation = useVoidCashTransaction();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: categories } = useExpenseCategories();
  const { data: summary } = useAccountsSummary(from, to);

  const { data: cashbook, isLoading, error, refetch } = useCashbook({
    from,
    to,
    types: ['expense'],
    category_id: selectedCategoryId || null,
    search: search.trim() || null,
    page,
    page_size: pageSize,
  });

  const handleVoidExpense = async (row: CashbookRow) => {
    const isOk = await confirm({
      title: 'Void Expense',
      description: `Are you sure you want to void expense "${row.particular}" for ₹${row.expense_amount}? This will restore the account balance.`,
      variant: 'destructive',
      confirmText: 'Void Expense',
    });
    if (!isOk) return;

    try {
      await voidMutation.mutateAsync({
        transaction_id: row.id,
        reason: 'Voided from Expenses list',
      });
      toast.success('Expense voided successfully');
      refetch();
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    }
  };

  const rows = cashbook?.rows ?? [];
  const totalExpenseAmount = cashbook?.totals.expense ?? 0;

  return (
    <div className="space-y-4">
      {/* Category Breakdown Chips / Summary Header */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="obsidian-surface rounded-xl border border-border bg-card p-4 shadow-xs">
          <span className="text-xs font-medium text-muted-foreground">Total Period Expenses</span>
          <div className="mt-1 text-xl font-bold text-rose-600 dark:text-rose-400">
            {formatINR(totalExpenseAmount)}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {cashbook?.total_rows ?? rows.length} expense items recorded
          </p>
        </div>

        <div className="obsidian-surface rounded-xl border border-border bg-card p-4 shadow-xs sm:col-span-2 flex flex-col justify-between">
          <div className="flex items-center justify-between pb-2">
            <span className="text-xs font-medium text-muted-foreground">Top Categories</span>
            <button
              type="button"
              onClick={() => onOpenRecordExpense(selectedCategoryId || undefined)}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              + Record Expense
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedCategoryId('')}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                !selectedCategoryId
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              All Categories
            </button>
            {categories?.map((cat) => {
              const isSelected = selectedCategoryId === cat.id;
              const catStat = summary?.categories.find((c) => c.category_id === cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategoryId(isSelected ? '' : cat.id)}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <span>{cat.name}</span>
                  {catStat && catStat.amount > 0 && (
                    <span className="text-[10px] opacity-75">₹{Math.round(catStat.amount)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search particular or notes..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-8 w-56 rounded-lg border border-border bg-card pl-8 pr-2.5 text-xs text-foreground placeholder:text-muted-foreground shadow-xs focus:border-primary focus:outline-none"
            helpLabel="Search records"
          />
        </div>

        <ExportActions
          excelOptions={() =>
            buildCashbookExcelOptions({
              rows,
              totals: cashbook?.totals,
              from,
              to,
              orgName: organization?.name,
            })
          }
          pdfOptions={() =>
            buildCashbookPdfOptions({
              rows,
              totals: cashbook?.totals,
              from,
              to,
              orgName: organization?.name,
            })
          }
        />
      </div>

      {/* Expenses Table */}
      {isLoading ? (
        <LoadingState message="Loading expenses…" />
      ) : error ? (
        <ErrorState message={error.message} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No expenses found"
          description="No expenses logged for the selected period or category."
          action={
            <button
              type="button"
              onClick={() => onOpenRecordExpense(selectedCategoryId || undefined)}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              + Record Expense
            </button>
          }
        />
      ) : (
        <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/40 font-medium text-muted-foreground">
                <tr>
                  <th className="py-2.5 px-3">Date</th>
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3">Particular / Description</th>
                  <th className="py-2.5 px-3">Paid From</th>
                  <th className="py-2.5 px-3">Method</th>
                  <th className="py-2.5 px-3 text-right">Amount (₹)</th>
                  {isOwnerOrAdmin && <th className="py-2.5 px-3 text-center w-10">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-3 font-mono text-muted-foreground whitespace-nowrap">
                      {formatDisplayDate(r.transaction_date)}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">
                        {categories?.find((c) => c.id === r.expense_category_id)?.name || 'Miscellaneous'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-medium text-foreground">
                      {r.particular}
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">
                      {r.account_name ?? '—'}
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground capitalize">
                      {r.payment_method?.replace(/_/g, ' ') || 'cash'}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-medium text-rose-600 dark:text-rose-400">
                      -{formatINR(r.expense_amount)}
                    </td>
                    {isOwnerOrAdmin && (
                      <td className="py-2.5 px-3 text-center">
                        {r.voidable ? (
                          <button
                            type="button"
                            onClick={() => handleVoidExpense(r)}
                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            title="Void expense"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <span className="text-muted-foreground/30 text-[10px]">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-border bg-muted/50 font-semibold text-foreground">
                <tr>
                  <td colSpan={5} className="py-2.5 px-3">
                    Total Expenses
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-rose-600 dark:text-rose-400">
                    -{formatINR(cashbook?.totals.expense)}
                  </td>
                  {isOwnerOrAdmin && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
